import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'

import { CreateTrip, TripItem } from '../types/trip'
import { loadTrips, modifyTrip, removeTrip, saveTrip } from '../api/trip'

const normalizeTrip = (trip: TripItem): TripItem => ({
  ...trip,
  latitude:
    typeof trip.latitude === 'number' && Number.isFinite(trip.latitude)
      ? trip.latitude
      : null,
  longitude:
    typeof trip.longitude === 'number' && Number.isFinite(trip.longitude)
      ? trip.longitude
      : null,
  image: trip.image || [],
  visitDates: trip.visitDates || [],
  notes: trip.notes || [],
  files: trip.files || [],
})

export const getTrips = createAsyncThunk('trip/getTrips', async (_, thunkApi) => {
  try {
    const trips = await loadTrips()
    return trips.map(normalizeTrip)
  } catch (e) {
    return thunkApi.rejectWithValue('getTripsError')
  }
})

export const addTrip = createAsyncThunk(
  'trip/addTrip',
  async (trip: CreateTrip, thunkApi) => {
    try {
      return normalizeTrip(await saveTrip(trip))
    } catch (e) {
      return thunkApi.rejectWithValue('addTripError')
    }
  },
)

export const updateTrip = createAsyncThunk(
  'trip/updateTrip',
  async (trip: TripItem, thunkApi) => {
    try {
      return normalizeTrip(await modifyTrip(trip))
    } catch (e) {
      return thunkApi.rejectWithValue('updateTripError')
    }
  },
)

export const deleteTrip = createAsyncThunk(
  'trip/deleteTrip',
  async (id: string, thunkApi) => {
    try {
      return await removeTrip(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteTripError')
    }
  },
)

interface TripSliceState {
  trips: TripItem[]
  loading: boolean
  error?: SerializedError
}

const initialState: TripSliceState = {
  trips: [],
  loading: false,
  error: undefined,
}

const tripSlice = createSlice({
  name: 'trip',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getTrips.fulfilled, (state, action: PayloadAction<TripItem[]>) => {
      state.trips = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getTrips.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getTrips.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addTrip.fulfilled, (state, action: PayloadAction<TripItem>) => {
      state.trips = [action.payload, ...state.trips]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addTrip.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addTrip.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateTrip.fulfilled, (state, action: PayloadAction<TripItem>) => {
      state.trips = state.trips.map((trip) =>
        trip.id === action.payload.id ? action.payload : trip,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateTrip.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateTrip.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteTrip.fulfilled, (state, action: PayloadAction<string>) => {
      state.trips = state.trips.filter((trip) => trip.id !== action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteTrip.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteTrip.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export default tripSlice.reducer
